import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const ProductFormModal = ({ product, onClose, onSaved }) => {
  const isEdit = Boolean(product);

  const [form, setForm] = useState({
    code: '',
    name: '',
    type: 'raw',
    category: '',
    base_unit: 'Kg',
    packaging_unit: '',
    packaging_qty: '',
    min_stock: '',
    default_price: '',
    is_active: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({
        code: product.code || '',
        name: product.name || '',
        type: product.type || 'raw',
        category: product.category || '',
        base_unit: product.base_unit || 'Kg',
        packaging_unit: product.packaging_unit || '',
        packaging_qty: product.packaging_qty || '',
        min_stock: product.min_stock || '',
        default_price: product.default_price || '',
        is_active: product.is_active !== undefined ? product.is_active : true,
      });
    }
  }, [product]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.code.trim() || !form.name.trim() || !form.type || !form.base_unit.trim()) {
      alert('❌ Code, Name, Type, and Base Unit are required.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...form,
        code: form.code.trim(),
        name: form.name.trim(),
        category: form.category.trim() || null,
        packaging_unit: form.packaging_unit.trim() || null,
        packaging_qty: form.packaging_qty === '' ? null : parseFloat(form.packaging_qty),
        min_stock: form.min_stock === '' ? null : parseFloat(form.min_stock),
        default_price: form.default_price === '' ? null : parseFloat(form.default_price),
      };

      if (isEdit) {
        await chemicalService.updateProduct(product.id, payload);
        alert('✅ Product updated successfully!');
      } else {
        await chemicalService.createProduct(payload);
        alert('✅ Product created successfully!');
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save product';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>
              {isEdit ? '✏️ Edit Product' : '➕ Add Product'}
            </h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              {isEdit ? `Updating ${product.code}` : 'Create a new product in the catalog'}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            {/* Row 1: Code + Name */}
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Code <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => handleChange('code', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. RM-014"
                  required
                />
              </div>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>
                  Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. Acetic Acid"
                  required
                />
              </div>
            </div>

            {/* Row 2: Type + Category */}
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Type <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  style={styles.input}
                  required
                >
                  <option value="raw">Raw material</option>
                  <option value="finished">Finished</option>
                  <option value="trading">Trading</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Category</label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. Reactive dye, Acid, Alkali"
                />
              </div>
            </div>

            {/* Row 3: Base Unit + Packaging */}
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Base Unit <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.base_unit}
                  onChange={(e) => handleChange('base_unit', e.target.value)}
                  style={styles.input}
                  required
                >
                  <option value="Kg">Kg</option>
                  <option value="Liter">Liter</option>
                  <option value="Gram">Gram</option>
                  <option value="ML">ML</option>
                  <option value="Piece">Piece</option>
                  <option value="Meter">Meter</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Packaging Unit</label>
                <select
                  value={form.packaging_unit}
                  onChange={(e) => handleChange('packaging_unit', e.target.value)}
                  style={styles.input}
                >
                  <option value="">None</option>
                  <option value="Bag">Bag</option>
                  <option value="Drum">Drum</option>
                  <option value="Container">Container</option>
                  <option value="Bottle">Bottle</option>
                  <option value="Carton">Carton</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Packaging Qty</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.packaging_qty}
                  onChange={(e) => handleChange('packaging_qty', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. 25"
                />
              </div>
            </div>

            {/* Helper line showing packaging preview */}
            {form.packaging_unit && form.packaging_qty && (
              <div style={styles.helperLine}>
                Packaging preview: <strong>{form.base_unit} • {form.packaging_unit} ({form.packaging_qty} {form.base_unit})</strong>
              </div>
            )}

            {/* Row 4: Min Stock + Default Price */}
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Min Stock (for low-stock alerts)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.min_stock}
                  onChange={(e) => handleChange('min_stock', e.target.value)}
                  style={styles.input}
                  placeholder={`e.g. 50 ${form.base_unit}`}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Default Price (per {form.base_unit})</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.default_price}
                  onChange={(e) => handleChange('default_price', e.target.value)}
                  style={styles.input}
                  placeholder="Rs"
                />
              </div>
            </div>

            {/* Row 5: Active toggle */}
            <div style={{ ...styles.formGroup, marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>
                  Active (uncheck to disable this product without deleting)
                </span>
              </label>
            </div>

          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" style={styles.submitButton} disabled={submitting}>
              {submitting ? '⏳ Saving...' : (isEdit ? '💾 Update Product' : '💾 Create Product')}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white',
    borderRadius: '12px',
    width: '95%',
    maxWidth: '760px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px',
    borderBottom: '2px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    position: 'sticky', top: 0, background: 'white', zIndex: 10,
  },
  modalTitle: {
    fontSize: '22px', fontWeight: '700',
    color: '#2c3e50', margin: 0,
  },
  closeButton: {
    padding: '8px 16px',
    background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '20px', fontWeight: '700', color: '#666',
  },
  row: {
    display: 'flex', gap: '16px', marginBottom: '4px',
    flexWrap: 'wrap',
  },
  formGroup: {
    flex: 1, minWidth: '180px',
    display: 'flex', flexDirection: 'column',
    gap: '6px', marginBottom: '16px',
  },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  required: { color: '#e74c3c' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  helperLine: {
    background: '#e8f5e9',
    color: '#155724',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '16px',
  },
  modalFooter: {
    padding: '16px 30px',
    borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
    position: 'sticky', bottom: 0, background: 'white',
  },
  cancelButton: {
    padding: '12px 24px',
    background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
  submitButton: {
    padding: '12px 28px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
};

export default ProductFormModal;